import React, { useMemo, useState } from 'react';
import { permissions } from '@vanilla/services/PermissionService.js';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import {
    Filter,
    Plus,
    SlidersHorizontal,
    Download,
    LayoutGrid,
    Table as TableIcon,
    MoreVertical,
    Check,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import PageHeader from '@/components/PageHeader.jsx';

function getActiveFilterCount(filters) {
    if (!filters) return 0;
    let count = 0;
    if (filters.scope === 'my') count++;
    if (filters.ownerIds && filters.ownerIds.length > 0) count++;
    if (filters.accountIds && filters.accountIds.length > 0) count++;
    if (filters.search && filters.search.trim().length > 0) count++;
    const dim = filters.dimension || 'created';
    const isDefaultTime =
        dim === 'created' &&
        filters.createdPeriodType === 'year' &&
        (filters.createdPeriodOffset === 0 || !filters.createdPeriodOffset) &&
        !filters.createdStart &&
        !filters.createdEnd;
    if (!isDefaultTime) count++;
    return count;
}

export default function DealsHeader({
    view,
    onViewChange,
    search = '',
    onSearchChange,
    summary,
    onNewDeal,
    onToggleFilter,
    filterCollapsed,
    filters,
    periodLabel,
    onDisplaySettings,
    onExport,
}) {
    const [showSummary, setShowSummary] = useState(false);

    const canExport = permissions.canExport('deals');
    const canCreate = permissions.canCreate('deals');
    const canViewFinancials = permissions.canViewFinancials('deals');

    const activeFilterCount = useMemo(() => getActiveFilterCount(filters), [filters]);

    const actions = (
        <>
            <Button
                type="button"
                variant={activeFilterCount > 0 ? 'secondary' : 'ghost'}
                size="sm"
                className={cn(
                    'h-8 px-3 sm:px-3.5 gap-1.5 rounded-lg text-[14px] font-medium border shrink-0 focus-visible:ring-2 py-1',
                    activeFilterCount > 0 ? 'bg-primary/10 border-primary/30 text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-slate-100'
                )}
                id="toggleFiltersBtn"
                aria-expanded={!filterCollapsed}
                onClick={onToggleFilter}
                title={activeFilterCount > 0 ? `${activeFilterCount} filters active` : 'Filters'}
            >
                <Filter className="size-4 shrink-0" strokeWidth={1.6} />
                <span className="hidden lg:inline text-[14px]">{activeFilterCount > 0 ? `${activeFilterCount}` : 'Filters'}</span>
                <span className="hidden xl:inline text-[14px]">{activeFilterCount > 0 ? (activeFilterCount === 1 ? ' filter' : ' filters') : ''}</span>
            </Button>

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 px-3 sm:px-3.5 rounded-lg text-[14px] font-medium gap-1.5 shrink-0 border border-transparent text-muted-foreground hover:text-foreground hover:bg-slate-100 py-1"
                        id="boardActionsBtn"
                        title="Board Actions"
                    >
                        <MoreVertical className="size-4 shrink-0" strokeWidth={1.6} />
                        <span className="hidden lg:inline text-[14px]">Board Actions</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={6} className="w-56 z-[500]">
                    <DropdownMenuLabel className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">View</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => onViewChange('kanban')} className={cn('text-xs gap-2 cursor-pointer', view === 'kanban' && 'font-semibold text-primary bg-accent/50')}>
                        <LayoutGrid className="size-3.5" /> Kanban {view === 'kanban' && <Check className="size-3.5 ml-auto" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onViewChange('table')} className={cn('text-xs gap-2 cursor-pointer', view === 'table' && 'font-semibold text-primary bg-accent/50')}>
                        <TableIcon className="size-3.5" /> Table {view === 'table' && <Check className="size-3.5 ml-auto" />}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {canViewFinancials && (
                        <>
                            <DropdownMenuItem
                                className="flex items-center justify-between gap-3 text-xs cursor-default focus:bg-transparent focus:text-foreground"
                                onSelect={(e) => e.preventDefault()}
                                onClick={(e) => e.preventDefault()}
                            >
                                <span className="text-xs font-medium">Revenue Summary</span>
                                <Switch
                                    checked={showSummary}
                                    onCheckedChange={setShowSummary}
                                    onClick={(e) => e.stopPropagation()}
                                    aria-label="Revenue Summary"
                                />
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                        </>
                    )}
                    <DropdownMenuItem onClick={onDisplaySettings} className="text-xs gap-2 cursor-pointer">
                        <SlidersHorizontal className="size-3.5" /> Display Columns
                    </DropdownMenuItem>
                    {canExport && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={onExport} className="text-xs gap-2 cursor-pointer">
                                <Download className="size-3.5" /> Export
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );

    const rightActions = canCreate ? (
        <Button type="button" variant="default" size="sm" className="h-8 px-3 sm:px-4 rounded-lg text-[14px] font-medium whitespace-nowrap gap-1.5 shrink-0 shadow-sm" id="createDealBtn" onClick={onNewDeal}>
            <Plus className="size-3.5 sm:size-4 shrink-0" strokeWidth={1.5} />
            <span className="hidden xs:inline sm:inline">New Deal</span>
            <span className="xs:hidden sm:hidden">New</span>
        </Button>
    ) : null;

    return (
        <>
            <PageHeader
                title="Deals"
                searchValue={search}
                onSearchChange={onSearchChange}
                searchPlaceholder="Search deals by name, Rep name, Account name"
                searchId="dealSearchInput"
                actions={actions}
                rightActions={rightActions}
            />
            {showSummary && canViewFinancials && (
                <div className="deal-header-summary-collapsible border-t border-border/60 bg-muted/25 px-3 sm:px-4 py-3" id="dealHeaderSummary" aria-live="polite">
                    <div className="grid grid-cols-2 md:flex md:items-stretch md:gap-8 gap-2.5 sm:gap-3">
                        <div className="deal-summary-stat flex flex-col p-2 sm:p-0 rounded-lg bg-card/75 sm:bg-transparent border sm:border-0 border-border/50">
                            <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Deal Amount</span>
                            <span className="text-sm sm:text-base font-bold text-foreground tabular-nums mt-0.5" id="dealSummaryTotalAmount">{summary?.totalAmount || '$0'}</span>
                        </div>
                        {summary?.revenueMode && (
                            <div className="deal-summary-stat flex flex-col p-2 sm:p-0 rounded-lg bg-card/75 sm:bg-transparent border sm:border-0 border-border/50" id="dealSummaryRevenueRegStat">
                                <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Revenue Reg</span>
                                <span className="text-sm sm:text-base font-bold text-emerald-600 tabular-nums mt-0.5" id="dealSummaryTotalRevenueReg">{summary?.revenueReg || '$0'}</span>
                            </div>
                        )}
                        <div className="deal-summary-stat flex flex-col p-2 sm:p-0 rounded-lg bg-card/75 sm:bg-transparent border sm:border-0 border-border/50">
                            <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Forecast Amount</span>
                            <span className="text-sm sm:text-base font-bold text-foreground tabular-nums mt-0.5" id="dealSummaryTotalForecast">{summary?.totalForecast || '$0'}</span>
                        </div>
                        {summary?.revenueMode && (
                            <div className="deal-summary-stat flex flex-col p-2 sm:p-0 rounded-lg bg-card/75 sm:bg-transparent border sm:border-0 border-border/50" id="dealSummaryRevenueRegForecastStat">
                                <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Revenue Reg Forecast</span>
                                <span className="text-sm sm:text-base font-bold text-emerald-600 tabular-nums mt-0.5" id="dealSummaryTotalRevenueRegForecast">{summary?.revenueRegForecast || '$0'}</span>
                            </div>
                        )}
                        <div className="deal-summary-stat flex flex-col p-2 sm:p-0 rounded-lg bg-card/75 sm:bg-transparent border sm:border-0 border-border/50 min-w-[140px]" id="dealSummaryBreakthroughGoal">
                            <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Breakeven Goal</span>
                            <span className="text-[11px] sm:text-xs font-medium text-foreground tabular-nums mt-0.5 truncate" title={`${summary?.breakthrough?.goalAmount || '$0'} / ${summary?.breakthrough?.achievedAmount || '$0'}`}>{summary?.breakthrough?.goalAmount || '$0'} / {summary?.breakthrough?.achievedAmount || '$0'}</span>
                            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mt-1.5"><div className="h-full bg-indigo-500 rounded-full transition-all duration-300" style={{ width: `${Math.min(100, summary?.breakthrough?.pct ?? 0)}%` }} /></div>
                            <span className="text-[10px] font-semibold text-indigo-600 tabular-nums mt-0.5">{summary?.breakthrough?.pct ?? 0}%</span>
                        </div>
                        <div className="deal-summary-stat flex flex-col p-2 sm:p-0 rounded-lg bg-card/75 sm:bg-transparent border sm:border-0 border-border/50 min-w-[140px]" id="dealSummaryProfitableGoal">
                            <span className="text-[10px] sm:text-xs font-semibold text-muted-foreground uppercase tracking-wider">Profitable Goal</span>
                            <span className="text-[11px] sm:text-xs font-medium text-foreground tabular-nums mt-0.5 truncate" title={`${summary?.profitable?.goalAmount || '$0'} / ${summary?.profitable?.achievedAmount || '$0'}`}>{summary?.profitable?.goalAmount || '$0'} / {summary?.profitable?.achievedAmount || '$0'}</span>
                            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mt-1.5"><div className="h-full bg-emerald-500 rounded-full transition-all duration-300" style={{ width: `${Math.min(100, summary?.profitable?.pct ?? 0)}%` }} /></div>
                            <span className="text-[10px] font-semibold text-emerald-600 tabular-nums mt-0.5">{summary?.profitable?.pct ?? 0}%</span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
